import { DATA_RESTRICTIONS } from '@bugbug/core/constants/dataRestrictions';
import { isRecordingOrPausedStatus } from '@bugbug/core/types/base';
import { useCallback, useRef } from 'react';

import type EditableText from '~/components/EditableText';

import { StatusBadgeFlex } from '~/components/RunMetadata/RunMetadata.styled';
import TestRunMetadata from '~/components/TestRunMetadata';
import useQueryString from '~/hooks/useQueryString';
import { useToggleStepsActivation } from '~/modules/steps/hooks/useToggleStepsActivation/useToggleStepsActivation';
import { useAppSelector } from '~/modules/store';
import { selectFailedStepRunWithStep } from '~/modules/testRun/testRun.selectors';
import { useCopyPasteSteps } from '~/modules/tests/hooks/useCopyPasteSteps/useCopyPasteSteps';
import { useUpdateTest } from '~/modules/tests/hooks/useUpdateTest/useUpdateTest';
import {
  selectCurrentTest,
  selectCurrentTestStepsActivation,
} from '~/modules/tests/tests.selectors';
import { selectCheckedStepsByGroups } from '~/modules/uiState/uiState.selectors';
import { getExtractedErrorMessages, isApiError } from '~/services/api/utils';

import { SelectedRowsActions as SelectedRowsActionsHeader } from '../SelectedRowsActions/SelectedRowsActions';
import { TestHeaderActions } from '../TestActions/TestHeaderActions';

import * as S from './TestHeader.styled';

interface TestHeaderProps {
  readOnly: boolean;
  onDeselect: () => void;
  onErrorClick?: () => void;
}

export const TestHeader = ({ onDeselect, onErrorClick, readOnly }: TestHeaderProps) => {
  const nameFieldRef = useRef<typeof EditableText>();
  const test = useAppSelector(selectCurrentTest)!;
  const stepsActivation = useAppSelector(selectCurrentTestStepsActivation);
  const checkedGroups = useAppSelector(selectCheckedStepsByGroups);
  const failedStepRunWithStep = useAppSelector(selectFailedStepRunWithStep);
  const failedStepId = failedStepRunWithStep?.failedStepRun?.stepId;
  const query = useQueryString();
  const { copySteps } = useCopyPasteSteps();
  const [toggleStepsActivation] = useToggleStepsActivation();
  const [updateTest] = useUpdateTest();

  const testRun = test?.testRun;
  const hasGroups = !!test?.groups?.length;

  const handleNameChange = useCallback(
    async (value) => {
      try {
        await updateTest(test.id, { name: value });
      } catch (error) {
        if (isApiError(error)) {
          const errors = getExtractedErrorMessages(error);
          // @ts-expect-error missing types
          nameFieldRef.current?.setError(errors.name);
        }
      }
    },
    [test.id, updateTest],
  );

  const handleStepsActivationChange = useCallback(
    async (steps, value) => {
      await toggleStepsActivation({ ids: steps, isActive: value, testId: test.id });
      onDeselect();
    },
    [toggleStepsActivation, test.id, onDeselect],
  );

  const handleStepsDelete = useCallback(() => {
    onDeselect();
  }, [onDeselect]);

  const handleCopySteps = useCallback(
    (stepIds) => {
      copySteps(stepIds);
    },
    [copySteps],
  );

  const isRecordingOrPaused = testRun && isRecordingOrPausedStatus(testRun.status);

  return (
    <S.Container data-testid="TestHeader">
      <S.Title>
        <S.NameContainer>
          {test?.name && (
            <S.NameInput
              data-hj-allow
              ref={nameFieldRef}
              // @ts-expect-error missing types
              value={test?.name}
              onChange={handleNameChange}
              readOnly={readOnly}
              max={DATA_RESTRICTIONS.TEST_NAME_MAX_LENGTH}
            />
          )}
        </S.NameContainer>
        <S.Actions>
          {Object.keys(checkedGroups).length > 0 ? (
            <SelectedRowsActionsHeader
              testId={test.id}
              onDeselect={onDeselect}
              stepsActivation={stepsActivation}
              onDelete={handleStepsDelete}
              onActivationChange={handleStepsActivationChange}
              onCopySteps={handleCopySteps}
            />
          ) : (
            <TestHeaderActions testId={test.id} testRunId={query.testRunId || testRun?.id} />
          )}
        </S.Actions>
      </S.Title>
      {hasGroups && isRecordingOrPaused && (
        <S.RecordingStatusWrapper>
          <StatusBadgeFlex status={testRun.status} />
        </S.RecordingStatusWrapper>
      )}
      {hasGroups && !isRecordingOrPaused && (
        <TestRunMetadata
          testRunId={testRun?.id}
          onErrorClick={failedStepId ? onErrorClick : undefined}
          variant="testDetails"
        />
      )}
    </S.Container>
  );
};
